// MBA Lab — Change and AI Readiness Assessment UI. Imports only lib/core.js and
// lib/readiness-model.js. Event delegation over data-readiness-dimension means
// the 12 radiogroups never need individually-wired listeners.
import { store, trackToolAction } from './lib/core.js';
import { hydrate, mountSyncIndicator, syncedStore } from './lib/account-sync.js';
import {
  TOTAL_DIMENSIONS,
  SCHEMA_VERSION,
  createInitialState,
  parseSavedState,
  serializeForSave,
  setStatus,
  countAnswered,
  buildReadout,
} from './lib/readiness-model.js';
import { DIMENSIONS } from './lib/readiness-dimensions.js';
import { initToolDock } from './lib/tool-dock.js';

const TOOL_NAME = 'change-readiness-assessment';
const AUTOSAVE_DEBOUNCE_MS = 400;
const ANNOUNCE_DEBOUNCE_MS = 1200;

function byId(id) {
  return document.getElementById(id);
}

function clearChildren(el) {
  while (el.firstChild) el.removeChild(el.firstChild);
}

class ChangeReadinessAssessment {
  constructor() {
    this.cacheElements();
    // Signed in: also syncs to the account (see lib/account-sync.js). Signed out, or with the
    // feature off, this is a pure passthrough to the same localStorage wrapper as before.
    this.readinessStore = syncedStore(TOOL_NAME, store(TOOL_NAME), {
      schemaVersion: SCHEMA_VERSION,
      onStatus: mountSyncIndicator(),
    });
    this.saveTimer = null;
    this.announceTimer = null;
    this.hasEngaged = false;
    this.hasFiredResult = false;
    this.dock = initToolDock({
      dock: byId('readiness-dock'),
      jumpTarget: byId('readiness-readout'),
      autoVisibility: false,
      hideJumpUntilComplete: true,
    });

    this.state = this.loadInitialState();
    this.applyStateToForm();
    this.renderProgress();
    this.renderReadoutIfComplete();
    this.wireEvents();
    trackToolAction(TOOL_NAME, 'open');
  }

  cacheElements() {
    this.form = byId('readiness-form');
    this.els = {
      progressText: byId('readiness-progress-text'),
      startOver: byId('readiness-start-over'),
      readoutSection: byId('readiness-readout'),
      readoutIncomplete: byId('readiness-readout-incomplete'),
      actionPlan: byId('readiness-action-plan'),
      actionPlanEmpty: byId('readiness-action-plan-empty'),
      notStarted: byId('readiness-group-not-started'),
      inProgress: byId('readiness-group-in-progress'),
      addressed: byId('readiness-group-addressed'),
      summaryAnnouncer: byId('readiness-summary-announcer'),
    };
  }

  loadInitialState() {
    const saved = this.readinessStore.load();
    if (!saved) return createInitialState();
    const result = parseSavedState(saved);
    if (result.status === 'ok' || result.status === 'migrated') return result.state;
    return createInitialState();
  }

  applyStateToForm() {
    for (const dim of DIMENSIONS) {
      const value = this.state.statuses[dim.key];
      if (!value) continue;
      const input = this.form.querySelector(`input[data-readiness-dimension="${dim.key}"][value="${value}"]`);
      if (input) input.checked = true;
    }
  }

  wireEvents() {
    this.form.addEventListener('change', (e) => this.handleStatusChange(e));
    this.els.startOver.addEventListener('click', () => this.handleStartOver());
  }

  handleStatusChange(e) {
    const el = e.target.closest('input[data-readiness-dimension]');
    if (!el || !this.form.contains(el)) return;
    this.state = setStatus(this.state, el.dataset.readinessDimension, el.value);
    this.markEngaged();
    this.renderProgress();
    this.renderReadoutIfComplete();
    this.scheduleAutosave();
  }

  markEngaged() {
    if (this.hasEngaged) return;
    this.hasEngaged = true;
    trackToolAction(TOOL_NAME, 'engage');
    this.dock?.show();
  }

  renderProgress() {
    const answered = countAnswered(this.state);
    this.els.progressText.textContent = `${answered} of ${TOTAL_DIMENSIONS} answered`;
    this.dock?.setReading(`${answered} of ${TOTAL_DIMENSIONS} answered`);
  }

  renderReadoutIfComplete() {
    const readout = buildReadout(this.state);
    this.dock?.setComplete(!!readout);
    if (!readout) {
      this.els.readoutSection.hidden = true;
      this.els.readoutIncomplete.hidden = false;
      if (this.announceTimer) clearTimeout(this.announceTimer);
      this.els.summaryAnnouncer.textContent = '';
      return;
    }

    this.els.readoutIncomplete.hidden = true;
    this.els.readoutSection.hidden = false;

    this.renderActionPlan(readout.actionPlan);
    this.renderGroup(this.els.notStarted, readout.notStarted);
    this.renderGroup(this.els.inProgress, readout.inProgress);
    this.renderGroup(this.els.addressed, readout.addressed);
    this.scheduleAnnouncement(
      `Your readiness profile: ${readout.notStarted.length} not yet started, ` +
        `${readout.inProgress.length} in progress, ${readout.addressed.length} addressed. ` +
        `${readout.actionPlan.length} priority items in your action plan.`
    );

    if (!this.hasFiredResult) {
      this.hasFiredResult = true;
      // Coarse counts only - never which specific dimensions, since that can
      // reveal sensitive internal-rollout detail about the user's own org.
      trackToolAction(TOOL_NAME, 'result', {
        not_started_count: readout.notStarted.length,
        in_progress_count: readout.inProgress.length,
        addressed_count: readout.addressed.length,
      });
    }
  }

  renderActionPlan(items) {
    clearChildren(this.els.actionPlan);
    this.els.actionPlanEmpty.hidden = items.length > 0;
    for (const item of items) {
      const li = document.createElement('li');
      li.className = 'tool-readiness-action-item';

      const label = document.createElement('strong');
      label.textContent = item.label;
      li.appendChild(label);

      const text = document.createElement('p');
      text.textContent = item.text;
      li.appendChild(text);

      this.els.actionPlan.appendChild(li);
    }
  }

  renderGroup(container, items) {
    clearChildren(container);
    for (const item of items) {
      const card = document.createElement('div');
      card.className = 'tool-readiness-group-card';

      // h3, not h4 - the containing "Not yet started"/"In progress"/"Addressed"
      // section header is an h2 (content-section-title), so this is the next
      // level down, not two.
      const title = document.createElement('h3');
      title.textContent = item.label;
      card.appendChild(title);

      const text = document.createElement('p');
      text.textContent = item.text;
      card.appendChild(text);

      container.appendChild(card);
    }
  }

  /**
   * Mirrors roi.js's scheduleAnnouncement: the visible groups/action-plan
   * update immediately, but a screen reader announcing every intermediate
   * change while someone revises earlier answers would be noisy. This mirrors
   * a summary into a separate visually-hidden aria-live region, debounced.
   */
  scheduleAnnouncement(text) {
    if (this.announceTimer) clearTimeout(this.announceTimer);
    this.announceTimer = setTimeout(() => {
      this.announceTimer = null;
      if (this.els.summaryAnnouncer.textContent !== text) {
        this.els.summaryAnnouncer.textContent = text;
      }
    }, ANNOUNCE_DEBOUNCE_MS);
  }

  scheduleAutosave() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.autosave(), AUTOSAVE_DEBOUNCE_MS);
  }

  autosave() {
    const toSave = { ...this.state, savedAt: Date.now() };
    const json = serializeForSave(toSave);
    if (!json) return;
    this.readinessStore.save(toSave);
  }

  handleStartOver() {
    // Start over means everywhere, not just here - so this is deleteAccountState rather than
    // clear(), which is local-only by design. Fire-and-forget: the local reset below is what the
    // person sees, and a failed delete is superseded by the next save.
    this.readinessStore.deleteAccountState();
    this.state = createInitialState();
    this.hasFiredResult = false;
    this.form.reset();
    this.renderProgress();
    this.renderReadoutIfComplete();
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  if (!byId('readiness-form')) return;
  // Reconcile with the account BEFORE the tool reads its own storage, so it simply loads whatever
  // won and needs no knowledge of syncing. A no-op (and no request) when signed out; bounded by
  // its own short timeout, so a slow network delays startup rather than blocking it.
  await hydrate(TOOL_NAME, store(TOOL_NAME), SCHEMA_VERSION);
  new ChangeReadinessAssessment();
});
